import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Text,
  Spinner,
  Flex,
  Box,
  ModalCloseButton,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  EntityTag,
  EntityTagCounts,
  tagsApi,
} from '@texas/api/endpoints/tagsApi';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { useEffect, useState } from 'react';
import { entityTypeFriendlyName } from '../shared/tracker/translations';
import { AlertDetails } from '../shared/alert/AlertDetails';

interface ArchiveTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  tag: EntityTag;
  onArchived: () => void;
}

export function ArchiveTagModal({
  isOpen,
  onClose,
  tag,
  onArchived,
}: ArchiveTagModalProps) {
  const { t } = useTranslation();
  const [isArchiving, setIsArchiving] = useState(false);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [entityCounts, setEntityCounts] = useState<EntityTagCounts | null>(
    null,
  );

  useEffect(() => {
    if (isOpen) {
      setLoadingCounts(true);
      tagsApi.getEntityCounts(tag.id).then((response) => {
        setLoadingCounts(false);
        if (!response.hasError) {
          setEntityCounts(response.data);
        }
      });
    }
  }, [isOpen, tag.id]);

  const handleArchive = async () => {
    setIsArchiving(true);
    const response = await tagsApi.archive(tag.id);
    setIsArchiving(false);
    if (!response.hasError) {
      onArchived();
      onClose();
    }
  };

  const hasLinkedEntities = entityCounts && entityCounts.totalCount > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('tags.archiveModal.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text>{t('tags.archiveModal.confirm', { label: tag.label })}</Text>

            <Box>
              <Text fontWeight="bold" mb={2}>
                {t('tags.archiveModal.linkedEntitiesTitle')}
              </Text>
              {loadingCounts && (
                <Flex>
                  <Spinner />
                </Flex>
              )}
              {!loadingCounts && hasLinkedEntities && (
                <>
                  <VStack align="stretch" spacing={1}>
                    {Object.entries(entityCounts.entityCounts).map(
                      ([entityType, count]) => {
                        const typeLabel = entityTypeFriendlyName(
                          t,
                          entityType as unknown as EntityType,
                        );
                        return (
                          <Text key={entityType}>
                            {count} {typeLabel}
                          </Text>
                        );
                      },
                    )}
                  </VStack>
                  <Text variant="sub">
                    {t('general.total')}: {entityCounts.totalCount}
                  </Text>
                </>
              )}
              {!loadingCounts && !hasLinkedEntities && (
                <Text variant="sub">
                  {t('tags.archiveModal.noLinkedEntities')}
                </Text>
              )}
            </Box>

            <AlertDetails
              type="info"
              description={t('tags.archiveModal.restoreInfo')}
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="texas-light"
            mr={3}
            onClick={onClose}
            isDisabled={isArchiving}
          >
            {t('general.cancel')}
          </Button>
          <Button
            variant="texas-solid"
            onClick={handleArchive}
            isLoading={isArchiving}
          >
            {t('general.archive')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
